import React, { useCallback, useMemo, useState } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import Text from "../../components/Text";
import { useTheme } from "../../hooks/use-theme";
import { Image, Platform, Share, View } from "react-native";
import Clipboard from "@react-native-clipboard/clipboard";
import HStack from "../../ui_atoms/HStack";
import TextField from "../../components/TextField";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";

const InviteClientInfoScreen = () => {
    const i18n = useI18n();
    const { fonts, colors, marginSize } = useTheme();
    const isLarge = useIsScreenSizeMin("large");

    const { title, message } = useMemo<{ title: string; message: string }>(
        () => ({
            title: i18n.t(`screens.invite_client.shared_title`),
            message: i18n.t(`screens.invite_client.shared_message`),
        }),
        [i18n],
    );

    const isShareAvailable = useMemo(
        () =>
            ["android", "ios"].includes(Platform.OS) ||
            navigator?.canShare?.({ title, text: message }),
        [message, title],
    );

    const onShare = useCallback(async () => {
        try {
            await Share.share({ title, message });
        } catch (_e) {
            // Share canceled should not trigger a Sentry error
            /* empty */
        }
    }, [title, message]);

    const [isCopied, setIsCopied] = useState(false);

    const onCopyText = useCallback(() => {
        Clipboard.setString(message);
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 1500);
    }, [message]);

    const { logoSize, logoMargin, fieldHeight } = useMemo(() => {
        if (isLarge) {
            return {
                logoSize: 150,
                logoMargin: -8,
                fieldHeight: 222,
            };
        } else {
            return {
                logoSize: 70,
                logoMargin: -3,
                fieldHeight: 335,
            };
        }
    }, [isLarge]);

    return (
        <ScreenCard
            contentStyle={{ paddingBottom: 0 }}
            style={{ paddingBottom: 0 }}
            buttons={[
                isShareAvailable
                    ? {
                          title: i18n.t("screens.invite_client.invite"),
                          mode: "secondary",
                          onPress: onShare,
                      }
                    : {
                          title: !isCopied
                              ? i18n.t("screens.invite_client.copy_text")
                              : i18n.t("screens.invite_client.copied"),
                          mode: "soft",
                          onPress: onCopyText,
                      },
            ]}
        >
            <VStack gap={1} h="2/3" v={0.5} style={{ flex: 1, paddingBottom: 0 }}>
                <Text style={fonts.bodyMedium}>{i18n.t("screens.invite_client.description")}</Text>
                {!isShareAvailable && (
                    <HStack alignment="top">
                        <Image
                            source={require("../../assets/images/lift_logo.png")}
                            style={{
                                width: logoSize,
                                height: logoSize,
                                margin: logoMargin,
                            }}
                        />
                        <View style={{ width: marginSize / 2 }} />
                        <TextField
                            isFocused
                            multiline
                            showSoftInputOnFocus={false}
                            selectTextOnFocus
                            spellCheck={false}
                            style={{ height: fieldHeight, flex: 1, marginTop: 0 }}
                            inputStyle={{ color: colors.greyAccessible }}
                            value={message}
                        />
                    </HStack>
                )}
            </VStack>
        </ScreenCard>
    );
};

export default InviteClientInfoScreen;
